// KPICard — Estilo idêntico ao sistema-producao
import React from 'react';
import { Typography } from '@mui/material';
import {
    AreaChart, Area, ResponsiveContainer
} from 'recharts';
import styles from './KPICard.module.css';

interface SparklinePoint {
    date: string;
    count: number;
}

interface KPICardProps {
    title: string;
    value: string;
    /** Valor secundário (ex: "R$ 1.234") */
    subValue?: string;
    /** Label auxiliar 1 (ex: "+12.5% vs anterior") */
    subLabel1?: string;
    /** Label auxiliar 2 (ReactNode) */
    subLabel2?: React.ReactNode;
    /** Ícone no canto superior direito */
    icon?: React.ReactNode;
    /** Se true, destaca o ícone (fundo vermelho) */
    accent?: boolean;
    onClick?: () => void;
    isActive?: boolean;
    /** Dados opcionais para sparkline (mini gráfico de área no fundo) */
    sparklineData?: SparklinePoint[];
    /** @deprecated Use subLabel1. Mantido para compatibilidade com outras páginas */
    percentageChange?: number;
    /** @deprecated Use subLabel1 */
    alwaysPositive?: boolean;
    hideTrendIcon?: boolean;
    customColor?: string;
}

const KPICard: React.FC<KPICardProps> = ({
    title,
    value,
    subValue,
    subLabel1,
    subLabel2,
    icon,
    accent = false,
    onClick,
    isActive,
    sparklineData,
    percentageChange,
    alwaysPositive,
}) => {
    // Compatibilidade: se percentageChange for passado, usa como subLabel1
    const pctValue = percentageChange;
    const resolvedSubLabel1 = subLabel1 ?? (
        pctValue !== undefined
            ? `${pctValue >= 0 ? '+' : ''}${pctValue.toFixed(1)}%${alwaysPositive ? '' : ' vs anterior'}`
            : undefined
    );

    // Detecta se o subLabel1 indica valor positivo ou negativo
    const isPctPositive = (resolvedSubLabel1?.startsWith('+') ?? false) || (alwaysPositive ?? false);
    const isPctNegative = resolvedSubLabel1?.startsWith('-') ?? false;
    return (
        <div
            className={`${styles.kpiCard} ${isActive ? styles.activeCard : ''} ${onClick ? styles.clickableCard : ''}`}
            onClick={onClick}
        >
            {/* Sparkline de fundo (igual sistema-producao) */}
            {sparklineData && sparklineData.length > 1 && (
                <div className={styles.sparklineContainer}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={sparklineData}>
                            <defs>
                                <linearGradient id={`spark-${title.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="var(--theme-primary, #b91c1c)" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="var(--theme-primary, #b91c1c)" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <Area
                                type="monotone"
                                dataKey="count"
                                stroke="var(--theme-primary, #b91c1c)"
                                strokeWidth={2}
                                fill={`url(#spark-${title.replace(/\s/g, '')})`}
                                isAnimationActive={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Header: título à esquerda, ícone à direita */}
            <div className={styles.kpiHeader}>
                <Typography className={styles.kpiTitle}>{title}</Typography>
                {icon && (
                    <div className={`${styles.iconBox} ${accent ? styles.iconBoxAccent : styles.iconBoxDefault}`}>
                        {icon}
                    </div>
                )}
            </div>

            {/* Content: valor + labels */}
            <div className={styles.kpiContent}>
                <Typography className={styles.kpiValue}>{value}</Typography>
                <div className={styles.kpiFooter}>
                    {resolvedSubLabel1 && (
                        <span
                            className={styles.subLabel}
                            style={{
                                color: isPctPositive ? '#10b981' : isPctNegative ? '#ef4444' : '#64748b',
                                fontWeight: 700
                            }}
                        >
                            {resolvedSubLabel1}
                        </span>
                    )}
                    {subLabel2 && <span className={styles.subLabel}>{subLabel2}</span>}
                </div>
            </div>

        </div>
    );
};

export default KPICard;
